import React, {useEffect, useState} from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {visuallyHidden} from '@mui/utils';

import {t} from 'i18next';

import AurButton from '../common/aur-button/AurButton';
import {
  formatCurrencyAmount,
  donutColor,
  formateNumber,
  getUTCDateTimeForDisplay,
} from '../../utils/utils';
import AurIcon, {IconColors, IconNames} from '../common/aur-icon/AurIcon';
import AurDonutchart from '../common/aur-donut-chart/AurDonutChart';
import {useDispatch, useSelector} from 'react-redux';
import {
  setShowBudgetTrackerAlertModal,
  setShowBudgetTrackerDeleteToast,
  setShowBudgetTrackerModal,
} from '../../redux/slices/budgetTrackerSlice';
import {RootState} from '../../redux/store';
import {
  getBudgetTrackerDetails,
  removeBudgetTrackerAlert,
} from '../../api/SelectiveSelfManagementService';
import {IBudgetTrackerResponse} from './model';
import AurLoadingIndicator from '../common/aur-loading-indicator/AurLoadingIndictor';
import {useToast} from '../../contexts/toastContext/ToastContext';
import {AxiosError} from 'axios';
import {
  BUDGET_AlERT_COUNT_UPTO_ONE,
  BUDGET_CONSUMPTON_PERCENTAGE,
} from '../../constants/constants';
import {AurLightTooltip} from '../common/aur-light-tooltip/aur-light-tooltip';

const MAX_MIN_DECIMAL_VALUE = 2;
const BudgetTrackerOverview = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const showBudgetTrackerModal = useSelector(
    (state: RootState) => state.budgetTrackerModal
  ).showBudgetTrackerModal;

  const budgetAlertCount = useSelector(
    (state: RootState) => state.budgetTrackerModal
  ).budgetAlertCount;

  const budgetAlertID = useSelector(
    (state: RootState) => state.budgetTrackerModal
  ).budgetAlertId;

  const {showToast} = useToast();
  const [budgetDetails, setBudgetDetails] = useState<
    IBudgetTrackerResponse['data'] | null
  >(null);
  const dispatch = useDispatch();
  const clientId = useSelector((state: RootState) => state.reports.clientId);
  const {id, name} = useSelector(
    (state: RootState) => state.invoiceModal.modalDropDownSelectedItem
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [color, setColor] = useState<string>('');

  const handleCloseModal = () => {
    dispatch(setShowBudgetTrackerModal(false));
  };

  const handleRemoveAlert = async (budgetAlertID: number) => {
    setIsLoading(true);
    try {
      const response = await removeBudgetTrackerAlert(budgetAlertID);
      console.log(response.data);
      if (budgetAlertCount > BUDGET_AlERT_COUNT_UPTO_ONE) {
        dispatch(setShowBudgetTrackerModal(false));
        dispatch(setShowBudgetTrackerAlertModal(true));
        dispatch(setShowBudgetTrackerDeleteToast(true));
      } else if (budgetAlertCount === BUDGET_AlERT_COUNT_UPTO_ONE) {
        dispatch(setShowBudgetTrackerModal(false));
        dispatch(setShowBudgetTrackerAlertModal(false));
        dispatch(setShowBudgetTrackerDeleteToast(true));
      }
    } catch (error) {
      const err = error as AxiosError;
      showToast(err.message, 'error', undefined, 'center');
    } finally {
      setIsLoading(false);
    }
  };
  const handleBackNavigation = () => {
    if (budgetAlertCount > 1) dispatch(setShowBudgetTrackerAlertModal(true));
    dispatch(setShowBudgetTrackerModal(false));
  };

  const fetchBudgetTrackerDetails = async () => {
    setIsLoading(true);
    try {
      const response = await getBudgetTrackerDetails(clientId as string, id);
      const budgetTrackerData = response?.data?.data;
      setBudgetDetails(budgetTrackerData);
    } catch (error) {
      const err = error as AxiosError;
      showToast(err.message, 'error', undefined, 'center');
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchBudgetTrackerDetails();
  }, []);

  useEffect(() => {
    const budgetConsumptionPercentage =
      budgetDetails &&
      budgetDetails?.percentageBudgetConsumed * BUDGET_CONSUMPTON_PERCENTAGE;
    const colorGrandTotal = donutColor(budgetConsumptionPercentage as number);
    setColor(colorGrandTotal);
  }, [budgetDetails]);

  const renderModalHeader = () => (
    <Box className="budget-title-section">
      <Box className="budget-section-back-button">
        <AurButton
          startIcon={
            <AurIcon
              icon={IconNames.ArrowBack}
              color={IconColors.Custom}
              customClassName="white-color"
            />
          }
          type="continue"
          size="Medium"
          onClick={handleBackNavigation}
          label={t(
            'ecaHomepageModals.budgetTracker.budgetTrackerOverview.backButton'
          )}
          classNames={`${budgetAlertCount > BUDGET_AlERT_COUNT_UPTO_ONE ? 'budget-tracker-modal-back-button' : 'hide-back-button'}`}
          dataTestId="budget-tracker-back-button"
          aria-describedby="back-button-desc"
        >
          <Typography id="back-button-desc" style={visuallyHidden}>
            {t(
              'ecaHomepageModals.budgetTracker.budgetTrackerOverview.closeButtonHint'
            )}
          </Typography>
        </AurButton>
        <Typography
          id="budget-modal-title"
          className="typo-text headline3_size budget-view-text"
          tabIndex={0}
        >
          {t('ecaHomepageModals.budgetTracker.budgetTrackerOverview.title')}
        </Typography>
      </Box>
      <Box>
        <span className="typo-text body4_size current-data-view" tabIndex={0}>
          {t(
            'ecaHomepageModals.budgetTracker.budgetTrackerOverview.dataCurrentAsOf'
          )}
          &nbsp;
        </span>
        <AurLightTooltip title={t('common.yearMonthDate')}>
          <span className="typo-text label2_size budget-view-text" tabIndex={0}>
            {getUTCDateTimeForDisplay(new Date())}
          </span>
        </AurLightTooltip>
      </Box>
    </Box>
  );
  const BudgetTrackerGridView = () => {
    return (
      <Box
        sx={{
          width: 531,
          backgroundColor: '#0C119',
          borderTop: '1px solid #263449',
        }}
        tabIndex={0}
      >
        <Table
          aria-label={t('common.budgetDataTable')}
          className="budget-tracker-table "
          tabIndex={0}
        >
          <TableBody>
            <TableRow>
              <TableCell className="Table-cell budget-view-text" tabIndex={0}>
                {t(
                  'ecaHomepageModals.budgetTracker.budgetTrackerOverview.currency'
                )}
              </TableCell>
              <TableCell className="Table-cell budget-view-text" tabIndex={0}>
                {budgetDetails?.currency}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="Table-cell budget-view-text" tabIndex={0}>
                {t(
                  'ecaHomepageModals.budgetTracker.budgetTrackerOverview.budgetLimit'
                )}
              </TableCell>
              <TableCell className="Table-cell budget-view-text" tabIndex={0}>
                {formatCurrencyAmount(
                  budgetDetails?.currency,
                  formateNumber(budgetDetails?.budgetLimit ?? 0, {
                    minimumFractionDigits: MAX_MIN_DECIMAL_VALUE,
                    maximumFractionDigits: MAX_MIN_DECIMAL_VALUE,
                  })
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="Table-cell budget-view-text" tabIndex={0}>
                {t(
                  'ecaHomepageModals.budgetTracker.budgetTrackerOverview.billed'
                )}
              </TableCell>
              <TableCell className="Table-cell budget-view-text" tabIndex={0}>
                {formatCurrencyAmount(
                  budgetDetails?.currency,
                  formateNumber(budgetDetails?.billedAmount ?? 0, {
                    minimumFractionDigits: MAX_MIN_DECIMAL_VALUE,
                    maximumFractionDigits: MAX_MIN_DECIMAL_VALUE,
                  })
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="Table-cell budget-view-text" tabIndex={0}>
                {t(
                  'ecaHomepageModals.budgetTracker.budgetTrackerOverview.notYetInvoiced'
                )}
              </TableCell>
              <TableCell className="Table-cell budget-view-text" tabIndex={0}>
                {formatCurrencyAmount(
                  budgetDetails?.currency,
                  formateNumber(budgetDetails?.nonInvoiceAmount ?? 0, {
                    minimumFractionDigits: MAX_MIN_DECIMAL_VALUE,
                    maximumFractionDigits: MAX_MIN_DECIMAL_VALUE,
                  })
                )}
              </TableCell>
            </TableRow>
            <TableRow
              sx={{
                '& td': {
                  borderBottom: '1px solid #8C9EBA',
                },
              }}
            >
              <TableCell className="Table-cell budget-view-text" tabIndex={0}>
                {t(
                  'ecaHomepageModals.budgetTracker.budgetTrackerOverview.grandTotal'
                )}
              </TableCell>
              <TableCell
                className="Table-cell"
                style={{color: color}}
                tabIndex={0}
                data-TestId="grand-total"
              >
                {formatCurrencyAmount(
                  budgetDetails?.currency,
                  formateNumber(budgetDetails?.grandTotal ?? 0, {
                    minimumFractionDigits: MAX_MIN_DECIMAL_VALUE,
                    maximumFractionDigits: MAX_MIN_DECIMAL_VALUE,
                  })
                )}
              </TableCell>
            </TableRow>

            <TableRow className="estimated-budget-section">
              <TableCell className="Table-cell budget-view-text" tabIndex={0}>
                {t(
                  'ecaHomepageModals.budgetTracker.budgetTrackerOverview.estimatedUnspentBudget'
                )}
              </TableCell>
              <TableCell className="Table-cell budget-view-text" tabIndex={0}>
                {formatCurrencyAmount(
                  budgetDetails?.currency,
                  formateNumber(budgetDetails?.estimatedUnspentBudget ?? 0, {
                    minimumFractionDigits: MAX_MIN_DECIMAL_VALUE,
                    maximumFractionDigits: MAX_MIN_DECIMAL_VALUE,
                  })
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    );
  };

  const renderDialogActions = () => (
    <DialogActions
      className="footer-action-container "
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        padding: '16px 32px',
      }}
    >
      <Typography className="typo-text body4_size support-text" tabIndex={0}>
        {t('ecaHomepageModals.budgetTracker.budgetTrackerOverview.supportText')}
      </Typography>

      <Stack direction="row" spacing={2}>
        {budgetAlertCount >= BUDGET_AlERT_COUNT_UPTO_ONE ? (
          <AurButton
            tabIndex={0}
            startIcon={
              <AurIcon
                icon={IconNames.Remove}
                color={IconColors.Custom}
                customClassName="light_blue_400"
              />
            }
            onClick={() => handleRemoveAlert(budgetAlertID)}
            type="exit"
            size="Medium"
            label={t(
              'ecaHomepageModals.budgetTracker.budgetTrackerOverview.removeAlertButton'
            )}
            classNames="budget-tracker-modal-remove-alerts-button"
            dataTestId="remove-alert-button"
            aria-describedby="remove-alert-desc"
          >
            <Typography
              tabIndex={0}
              id="remove-alert-desc"
              role="status"
              aria-live="polite"
              aria-atomic="true"
              style={visuallyHidden}
              sx={{
                position: 'absolute',
                overflow: 'hidden',
              }}
            >
              {t(
                'ecaHomepageModals.budgetTracker.budgetTrackerOverview.removeButtonHint'
              )}{' '}
              {t(
                'ecaHomepageModals.budgetTracker.budgetTrackerOverview.projectName'
              )}
            </Typography>
          </AurButton>
        ) : null}
        <AurButton
          type="continue"
          size="Medium"
          onClick={handleCloseModal}
          label={t(
            'ecaHomepageModals.budgetTracker.budgetTrackerOverview.closeButton'
          )}
          classNames="budget-tracker-modal-close-button"
          dataTestId="budget-tracker-close-button"
          aria-describedby="close-button-desc"
        >
          <Typography id="close-button-desc" style={visuallyHidden}>
            {t(
              'ecaHomepageModals.budgetTracker.budgetTrackerOverview.closeButtonHint'
            )}
          </Typography>
        </AurButton>
      </Stack>
    </DialogActions>
  );

  return (
    <Dialog
      fullScreen={fullScreen}
      open={showBudgetTrackerModal}
      onClose={handleCloseModal}
      tabIndex={0}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: '#1F2B3D',
            backgroundImage: 'none',
          },
        },
      }}
      PaperProps={{
        className: 'budget-tracker-container',
        sx: {
          backgroundImage: 'none',
        },
      }}
      maxWidth={false}
      aria-labelledby="budget-modal-title"
      aria-describedby="budget-modal-description"
    >
      <AurLoadingIndicator show={isLoading} />
      <DialogContent className="model-content">
        {renderModalHeader()}
        <Box className="budget-tracker-detail-section">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box className="budget-tracter-detail-section">
                <Typography
                  className="typo-text label2_size project-name"
                  tabIndex={0}
                >
                  {t(
                    'ecaHomepageModals.budgetTracker.budgetTrackerOverview.projectName'
                  )}
                </Typography>
                <Typography
                  className="typo-text subhead3_size project-name-text budget-view-text"
                  tabIndex={0}
                >
                  {name}
                </Typography>

                <Typography
                  component="div"
                  className="typo-text label2_size engagement-code-text"
                  tabIndex={0}
                >
                  {t(
                    'ecaHomepageModals.budgetTracker.budgetTrackerOverview.engagementCode'
                  )}
                </Typography>
                <Typography
                  tabIndex={0}
                  component="div"
                  className="typo-text subhead3_size  project-name-text budget-view-text"
                >
                  {budgetDetails?.engagementCode}
                </Typography>
              </Box>
              <Box className="tabular-budget-details">
                {BudgetTrackerGridView()}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className="dognut-section" tabIndex={0}>
                <AurDonutchart
                  percentageValue={
                    budgetDetails?.percentageBudgetConsumed as number
                  }
                  backgroundColor="#0C1119"
                  width={300}
                  height={300}
                  customClassDonutContainer="donut-container-modal-view"
                  fieldName1="Percent"
                  fieldName2="of current limit:"
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      {budgetDetails?.percentageBudgetConsumed !== undefined ? (
        <Box className="disclaimer-text">
          <ul>
            <li>
              <Typography className="typo-text body4_size" tabIndex={0}>
                {t(
                  'ecaHomepageModals.budgetTracker.budgetTrackerOverview.disclaimerText1'
                )}
              </Typography>
            </li>
            <li>
              <Typography className="typo-text body4_size" tabIndex={0}>
                {t(
                  'ecaHomepageModals.budgetTracker.budgetTrackerOverview.disclaimerText2'
                )}
              </Typography>
            </li>
            <li>
              <Typography className="typo-text body4_size" tabIndex={0}>
                {t(
                  'ecaHomepageModals.budgetTracker.budgetTrackerOverview.disclaimerText3'
                )}
              </Typography>
            </li>
          </ul>
        </Box>
      ) : null}
      {budgetDetails?.percentageBudgetConsumed !== undefined
        ? renderDialogActions()
        : null}
    </Dialog>
  );
};

export default BudgetTrackerOverview;
